import { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { supplierInvoiceAPI } from "@/lib/api";
import {
  Upload, FileText, Loader2, CheckCircle, AlertTriangle, Sparkles, Receipt,
  ChevronDown, ChevronUp, Save, X, Check
} from "lucide-react";
import { InvoiceForm, FormButtons } from "@/components/supplier-invoice/InvoiceForm";

const DEFAULT_TYPES = {
  frais_repas: "Frais de repas", deplacements: "Deplacements", loyer_charges: "Loyer & charges",
  achats_marchandises: "Achats marchandises", informatique_telecom: "Informatique & telecom",
  honoraires: "Honoraires", entretien_reparations: "Entretien & reparations",
  fournitures_bureau: "Fournitures de bureau", publicite_marketing: "Publicite & marketing",
  assurances: "Assurances", energie: "Energie", formation: "Formation",
  sous_traitance: "Sous-traitance", divers: "Divers",
};

const emptyForm = {
  supplier_name: "", invoice_number: "", date: new Date().toISOString().slice(0, 10),
  type: "divers", description: "", total_ht: "", tva_rate: "20", tva_amount: "",
  total_ttc: "", payment_method: "cb", is_paid: true, notes: "", items: [],
  original_filename: "", tva_verification: {}, create_supplier: true,
};

function dataToForm(data, filename) {
  return {
    supplier_name: data.supplier_name || "",
    invoice_number: data.invoice_number || "",
    date: data.date || new Date().toISOString().slice(0, 10),
    type: data.type || "divers",
    description: data.description || "",
    total_ht: String(data.total_ht || ""),
    tva_rate: String(data.tva_rate || "20"),
    tva_amount: String(data.tva_amount || ""),
    total_ttc: String(data.total_ttc || ""),
    payment_method: data.payment_method || "cb",
    is_paid: data.is_paid !== undefined ? data.is_paid : true,
    notes: "", items: data.items || [],
    original_filename: filename || "",
    tva_verification: data.tva_verification || {},
  };
}

export function SupplierInvoiceDialog({ open, onClose, onCreated, editData, types }) {
  const [mode, setMode] = useState("choose"); // choose | manual | scan | batch-review
  const [form, setForm] = useState({ ...emptyForm });
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tvaCheck, setTvaCheck] = useState(null);
  const fileRef = useRef(null);
  const multiFileRef = useRef(null);
  const typeMap = types && Object.keys(types).length > 0 ? types : DEFAULT_TYPES;

  // Batch scan state
  const [batchItems, setBatchItems] = useState([]); // [{form, tvaCheck, filename, status:'pending'|'done'|'error'|'saved'}]
  const [scanProgress, setScanProgress] = useState({ done: 0, total: 0 });
  const [expandedIdx, setExpandedIdx] = useState(null);

  // Check if supplier exists and set create_supplier accordingly
  const checkAndSetSupplier = async (formData) => {
    if (!formData.supplier_name) return formData;
    try {
      const res = await supplierInvoiceAPI.checkSupplier(formData.supplier_name);
      return { ...formData, create_supplier: !(res.data?.exists) };
    } catch { return formData; }
  };


  useEffect(() => {
    if (open) {
      if (editData) {
        setForm({
          ...emptyForm, ...editData,
          total_ht: String(editData.total_ht || ""),
          tva_rate: String(editData.tva_rate || "20"),
          tva_amount: String(editData.tva_amount || ""),
          total_ttc: String(editData.total_ttc || ""),
        });
        setMode("manual");
        setTvaCheck(editData.tva_verification || null);
      } else {
        setForm({ ...emptyForm });
        setMode("choose");
        setTvaCheck(null);
        setBatchItems([]);
        setScanProgress({ done: 0, total: 0 });
        setExpandedIdx(null);
      }
    }
  }, [open, editData]);

  // Single file scan (for manual mode upload)
  const handleSingleFileUpload = async (file) => {
    if (!file) return;
    setScanning(true);
    setMode("scan");
    try {
      const res = await supplierInvoiceAPI.analyze(file);
      const resData = res.data || {};

      // Async job for multi-page PDF
      if (resData.is_async && resData.job_id) {
        const totalPages = resData.total_pages || 0;
        setScanProgress({ done: 0, total: totalPages });
        // Poll for results
        const pollResult = await pollAnalysisJob(resData.job_id, totalPages);
        if (pollResult) {
          const rows = pollResult.extracted_data || [];
          if (Array.isArray(rows) && rows.length > 0) {
            const items = [];
            for (const r of rows) {
              const f = await checkAndSetSupplier(dataToForm(r, file.name));
              items.push({ form: f, tvaCheck: r.tva_verification || null, filename: `${file.name} (${r.invoice_number || items.length + 1})`, status: "done" });
            }
            setBatchItems(items);
            setExpandedIdx(0);
            setMode("batch-review");
            setScanning(false);
            toast.success(`${items.length} facture${items.length > 1 ? "s" : ""} extraite${items.length > 1 ? "s" : ""}`);
            return;
          }
        }
        toast.error("Erreur lors de l'analyse du PDF multi-pages");
        setMode("manual");
        setScanning(false);
        return;
      }

      // Multi-invoice PDF or CSV returns an array
      if (resData.is_multi || resData.is_csv) {
        const rows = resData.extracted_data || [];
        if (!Array.isArray(rows) || rows.length === 0) {
          toast.error(resData.error || "Document vide ou non reconnu");
          setMode("manual");
        } else {
          const items = [];
          for (const r of rows) {
            const f = await checkAndSetSupplier(dataToForm(r, file.name));
            items.push({ form: f, tvaCheck: r.tva_verification || null, filename: `${file.name}${rows.length > 1 ? ` (${r.invoice_number || items.length + 1})` : ""}`, status: "done" });
          }
          setBatchItems(items);
          setExpandedIdx(0);
          setMode("batch-review");
          toast.success(`${items.length} facture${items.length > 1 ? "s" : ""} extraite${items.length > 1 ? "s" : ""}`);
        }
      } else {
        const data = resData.extracted_data || {};
        if (data.error || data.raw_response) {
          toast.error("L'IA n'a pas pu analyser correctement le document");
          setMode("manual");
          setForm(prev => ({ ...prev, original_filename: file.name }));
        } else {
          const checked = await checkAndSetSupplier(dataToForm(data, file.name));
          setForm(checked);
          setTvaCheck(data.tva_verification || null);
          setMode("review-single");
          toast.success("Analyse terminee !");
        }
      }
    } catch {
      toast.error("Erreur lors de l'analyse");
      setMode("manual");
      setForm(prev => ({ ...prev, original_filename: file.name }));
    } finally {
      setScanning(false);
    }
  };

  // Poll for async analysis job
  const pollAnalysisJob = async (jobId, totalPages) => {
    const maxAttempts = totalPages * 8 + 30; // ~8 attempts per page + buffer
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(r => setTimeout(r, 3000)); // Poll every 3s
      try {
        const res = await supplierInvoiceAPI.analyzeStatus(jobId);
        const data = res.data || {};
        setScanProgress({ done: data.progress || 0, total: data.total || totalPages });
        if (data.status === "done") return data;
        if (data.status === "error") { toast.error(data.error || "Erreur"); return null; }
        if (data.status === "not_found") return null;
      } catch (err) { console.error("Polling retry:", err); /* retry silently until timeout */ }
    }
    toast.error("Timeout: l'analyse a pris trop de temps");
    return null;
  };

  // Multi-file scan
  const handleMultiFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    const fileList = Array.from(files);
    setMode("scan");
    setScanning(true);
    setScanProgress({ done: 0, total: fileList.length });

    const results = [];
    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      try {
        const res = await supplierInvoiceAPI.analyze(file);
        const resData = res.data || {};

        if (resData.is_async && resData.job_id) {
          // Multi-page PDF async job within multi-file upload
          const totalPages = resData.total_pages || 0;
          const pollResult = await pollAnalysisJob(resData.job_id, totalPages);
          if (pollResult) {
            const rows = pollResult.extracted_data || [];
            if (Array.isArray(rows)) {
              for (const r of rows) {
                const f = await checkAndSetSupplier(dataToForm(r, file.name));
                results.push({ form: f, tvaCheck: r.tva_verification || null, filename: `${file.name} (${r.invoice_number || results.length + 1})`, status: "done" });
              }
            }
          }
        } else if (resData.is_csv || resData.is_multi) {
          // CSV or multi-invoice PDF: one file produces multiple invoices
          const rows = resData.extracted_data || [];
          if (Array.isArray(rows)) {
            for (const r of rows) {
              const f = await checkAndSetSupplier(dataToForm(r, file.name));
              results.push({ form: f, tvaCheck: r.tva_verification || null, filename: `${file.name}${rows.length > 1 ? ` (${r.invoice_number || results.length + 1})` : ""}`, status: "done" });
            }
          }
        } else {
          const data = resData.extracted_data || {};
          if (data.error || data.raw_response) {
            results.push({ form: { ...emptyForm, original_filename: file.name }, tvaCheck: null, filename: file.name, status: "error" });
          } else {
            const f = await checkAndSetSupplier(dataToForm(data, file.name));
            results.push({ form: f, tvaCheck: data.tva_verification || null, filename: file.name, status: "done" });
          }
        }
      } catch (err) {
        console.error("Batch scan error:", err);
        results.push({ form: { ...emptyForm, original_filename: file.name }, tvaCheck: null, filename: file.name, status: "error" });
      }
      setScanProgress({ done: i + 1, total: fileList.length });
    }

    setBatchItems(results);
    setExpandedIdx(0);
    setScanning(false);

    if (fileList.length === 1 && results.length === 1 && results[0]?.status === "done" && !fileList[0].name?.toLowerCase().endsWith(".csv")) {
      // Single non-CSV file → single review
      setForm(results[0].form);
      setTvaCheck(results[0].tvaCheck);
      setMode("review-single");
      toast.success("Analyse terminee !");
    } else {
      setMode("batch-review");
      const ok = results.filter(r => r.status === "done").length;
      const err = results.filter(r => r.status === "error").length;
      toast.success(`${ok} facture${ok > 1 ? "s" : ""} analysee${ok > 1 ? "s" : ""}${err ? `, ${err} erreur${err > 1 ? "s" : ""}` : ""}`);
    }
  };

  const recalcTva = () => {
    const ht = parseFloat(form.total_ht) || 0;
    const rate = parseFloat(form.tva_rate) || 0;
    const computed = Math.round(ht * rate / 100 * 100) / 100;
    setForm(prev => ({ ...prev, tva_amount: String(computed), total_ttc: String(Math.round((ht + computed) * 100) / 100) }));
  };

  const recalcBatchTva = (idx) => {
    setBatchItems(prev => {
      const items = [...prev];
      const f = items[idx].form;
      const ht = parseFloat(f.total_ht) || 0;
      const rate = parseFloat(f.tva_rate) || 0;
      const computed = Math.round(ht * rate / 100 * 100) / 100;
      items[idx] = { ...items[idx], form: { ...f, tva_amount: String(computed), total_ttc: String(Math.round((ht + computed) * 100) / 100) } };
      return items;
    });
  };

  const updateBatchField = (idx, field, value) => {
    setBatchItems(prev => {
      const items = [...prev];
      items[idx] = { ...items[idx], form: { ...items[idx].form, [field]: value } };
      return items;
    });
  };

  // Save single
  const handleSave = async () => {
    if (!form.total_ttc && !form.total_ht) {
      toast.error("Veuillez renseigner au moins le montant");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, total_ht: parseFloat(form.total_ht) || 0, tva_rate: parseFloat(form.tva_rate) || 0, tva_amount: parseFloat(form.tva_amount) || 0, total_ttc: parseFloat(form.total_ttc) || 0, create_supplier: form.create_supplier && !!form.supplier_name };
      if (editData?.id) {
        await supplierInvoiceAPI.update(editData.id, payload);
        toast.success("Facture mise à jour");
      } else {
        await supplierInvoiceAPI.create(payload);
        toast.success(form.create_supplier && form.supplier_name ? "Facture + fournisseur enregistrés" : "Facture enregistrée");
      }
      onCreated();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Erreur lors de l'enregistrément");
    } finally {
      setSaving(false);
    }
  };

  // Save all batch
  const handleSaveAll = async () => {
    const toSave = batchItems.filter(b => b.status === "done");
    if (toSave.length === 0) { toast.error("Aucune facture à enregistrer"); return; }
    setSaving(true);
    let saved = 0;
    for (let i = 0; i < batchItems.length; i++) {
      if (batchItems[i].status !== "done") continue;
      const f = batchItems[i].form;
      try {
        await supplierInvoiceAPI.create({ ...f, total_ht: parseFloat(f.total_ht) || 0, tva_rate: parseFloat(f.tva_rate) || 0, tva_amount: parseFloat(f.tva_amount) || 0, total_ttc: parseFloat(f.total_ttc) || 0, create_supplier: f.create_supplier !== false && !!f.supplier_name });
        setBatchItems(prev => { const items = [...prev]; items[i] = { ...items[i], status: "saved" }; return items; });
        saved++;
      } catch (err) {
        const detail = err?.response?.data?.detail;
        toast.error(detail || `Erreur sur ${f.original_filename || "facture"}`);
      }
    }
    setSaving(false);
    toast.success(`${saved} facture${saved > 1 ? "s" : ""} enregistrée${saved > 1 ? "s" : ""}`);
    if (saved === toSave.length) onCreated();
  };

  const u = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const formatEur = (v) => { const n = parseFloat(v) || 0; return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n); };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className={`max-h-[90vh] overflow-y-auto ${mode === "batch-review" ? "max-w-2xl" : "max-w-lg"}`} data-testid="supplier-invoice-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Receipt size={16} className="text-indigo-500" />
            {editData ? "Modifier la facture" : mode === "batch-review" ? `${batchItems.length} factures scannées` : "Nouvelle facture fournisseur"}
          </DialogTitle>
        </DialogHeader>

        {/* Mode chooser */}
        {mode === "choose" && (
          <div className="space-y-3 py-2" data-testid="mode-chooser">
            <p className="text-sm text-slate-500">Comment ajouter la facture ?</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <button
                onClick={() => multiFileRef.current?.click()}
                className="flex flex-col items-center gap-2 p-5 rounded-lg border-2 border-dashed border-slate-200 hover:border-indigo-400 hover:bg-indigo-50/50 transition-all"
                data-testid="mode-scan-btn"
              >
                <Sparkles size={20} className="text-indigo-600" />
                <span className="font-medium text-slate-900 text-sm">Scanner (IA)</span>
                <span className="text-[11px] text-slate-500 text-center">PDF, images ou CSV</span>
              </button>
              <button
                onClick={() => setMode("manual")}
                className="flex flex-col items-center gap-2 p-5 rounded-lg border-2 border-dashed border-slate-200 hover:border-slate-400 hover:bg-slate-50 transition-all"
                data-testid="mode-manual-btn"
              >
                <FileText size={20} className="text-slate-600" />
                <span className="font-medium text-slate-900 text-sm">Saisie manuelle</span>
                <span className="text-[11px] text-slate-500 text-center">Remplir soi-meme</span>
              </button>
            </div>
            <input ref={multiFileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.csv" multiple className="hidden"
              onChange={(e) => e.target.files?.length && handleMultiFileUpload(e.target.files)} data-testid="file-input-multi" />
          </div>
        )}

        {/* Scanning progress */}
        {mode === "scan" && scanning && (
          <div className="py-10 text-center space-y-3" data-testid="scanning-state">
            <Loader2 size={32} className="animate-spin mx-auto text-indigo-500" />
            <p className="font-medium text-slate-900 text-sm">
              Analyse en cours... {scanProgress.total > 1 && `(${scanProgress.done}/${scanProgress.total} pages)`}
            </p>
            {scanProgress.total > 1 && (
              <div className="w-48 mx-auto bg-slate-200 rounded-full h-1.5">
                <div className="bg-indigo-600 h-1.5 rounded-full transition-all" style={{ width: `${(scanProgress.done / scanProgress.total) * 100}%` }} />
              </div>
            )}
            <p className="text-xs text-slate-500">
              {scanProgress.total > 2 ? "L'IA analyse chaque page du document..." : "L'IA extrait les informations du document"}
            </p>
          </div>
        )}

        {/* Batch review (multiple files) */}
        {mode === "batch-review" && (
          <div className="space-y-3" data-testid="batch-review-mode">
            {/* Summary bar */}
            <div className="flex items-center gap-2 text-xs">
              <Badge variant="outline" className="gap-1">
                <Check size={10} /> {batchItems.filter(b => b.status === "done").length} prête{batchItems.filter(b => b.status === "done").length > 1 ? "s" : ""}
              </Badge>
              {batchItems.some(b => b.status === "saved") && (
                <Badge className="bg-emerald-100 text-emerald-700 gap-1">
                  <CheckCircle size={10} /> {batchItems.filter(b => b.status === "saved").length} enregistrée{batchItems.filter(b => b.status === "saved").length > 1 ? "s" : ""}
                </Badge>
              )}
              {batchItems.some(b => b.status === "error") && (
                <Badge variant="destructive" className="gap-1">
                  <AlertTriangle size={10} /> {batchItems.filter(b => b.status === "error").length} erreur{batchItems.filter(b => b.status === "error").length > 1 ? "s" : ""}
                </Badge>
              )}
            </div>

            {/* List of scanned invoices */}
            <div className="space-y-2 max-h-[55vh] overflow-y-auto" data-testid="batch-list">
              {batchItems.map((item, idx) => (
                <div key={item.id || item.supplier_ref || `batch-${idx}`} className={`border rounded-lg overflow-hidden transition-colors ${item.status === "saved" ? "border-emerald-200 bg-emerald-50/30" : item.status === "error" ? "border-red-200 bg-red-50/30" : "border-slate-200"}`} data-testid={`batch-item-${idx}`}>
                  {/* Collapsed header */}
                  <button
                    onClick={() => setExpandedIdx(expandedIdx === idx ? null : idx)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-slate-50/50"
                    data-testid={`batch-toggle-${idx}`}
                  >
                    {item.status === "saved" ? <CheckCircle size={14} className="text-emerald-500 flex-shrink-0" /> :
                     item.status === "error" ? <AlertTriangle size={14} className="text-red-400 flex-shrink-0" /> :
                     <FileText size={14} className="text-slate-400 flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-800 truncate">{item.filename}</p>
                      <p className="text-[11px] text-slate-500 truncate">
                        {typeMap[item.form.type] || item.form.type} — {item.form.supplier_name || "Sans fournisseur"} — {formatEur(item.form.total_ttc)} TTC
                      </p>
                    </div>
                    <span className="text-xs font-semibold text-emerald-600 tabular-nums flex-shrink-0 mr-2">
                      TVA {formatEur(item.form.tva_amount)}
                    </span>
                    {expandedIdx === idx ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
                  </button>

                  {/* Expanded form */}
                  {expandedIdx === idx && item.status !== "saved" && (
                    <div className="px-3 pb-3 border-t border-slate-100">
                      {item.tvaCheck && (
                        <div className={`p-2 rounded mt-2 text-[11px] flex items-center gap-1.5 ${item.tvaCheck.match ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                          {item.tvaCheck.match ? <CheckCircle size={12} /> : <AlertTriangle size={12} />}
                          {item.tvaCheck.match ? "TVA vérifiée" : `Ecart TVA: calculee ${item.tvaCheck.computed_tva} / doc ${item.tvaCheck.document_tva}`}
                        </div>
                      )}
                      <div className="mt-2">
                        <InvoiceForm
                          form={item.form}
                          u={(field, value) => updateBatchField(idx, field, value)}
                          typeMap={typeMap}
                          recalcTva={() => recalcBatchTva(idx)}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Batch action buttons */}
            <div className="flex gap-2 justify-end pt-1 border-t border-slate-100">
              <Button variant="outline" size="sm" onClick={onClose} data-testid="batch-cancel-btn">Fermer</Button>
              {batchItems.some(b => b.status === "done") && (
                <Button size="sm" onClick={handleSaveAll} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-all-btn">
                  {saving ? <Loader2 size={13} className="animate-spin mr-1" /> : <Save size={13} className="mr-1" />}
                  Enregistrer tout ({batchItems.filter(b => b.status === "done").length})
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Single review after AI scan */}
        {mode === "review-single" && (
          <div className="space-y-3" data-testid="review-mode">
            {tvaCheck && (
              <div className={`p-2.5 rounded-lg border text-xs flex items-start gap-2 ${tvaCheck.match ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-800"}`} data-testid="tva-verification-alert">
                {tvaCheck.match ? <CheckCircle size={14} className="mt-0.5 flex-shrink-0" /> : <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />}
                <div>
                  <p className="font-medium">{tvaCheck.match ? "TVA vérifiée" : "Ecart TVA détecté"}</p>
                  {tvaCheck.comment && <p className="mt-0.5">{tvaCheck.comment}</p>}
                  {!tvaCheck.match && <p className="mt-0.5">Calculee: {tvaCheck.computed_tva} / Document: {tvaCheck.document_tva}</p>}
                </div>
              </div>
            )}
            {form.original_filename && (
              <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 px-3 py-1.5 rounded">
                <FileText size={12} /> {form.original_filename}
                <Badge variant="outline" className="ml-auto text-[10px]">IA</Badge>
              </div>
            )}
            <InvoiceForm form={form} u={u} typeMap={typeMap} recalcTva={recalcTva} />
            <FormButtons onClose={onClose} onSave={handleSave} saving={saving} label="Enregistrer" />
          </div>
        )}

        {/* Manual mode */}
        {mode === "manual" && (
          <div className="space-y-3" data-testid="manual-mode">
            {!editData && (
              <button onClick={() => fileRef.current?.click()}
                className="flex items-center gap-1.5 text-xs text-indigo-600 hover:text-indigo-700" data-testid="upload-in-manual-btn">
                <Upload size={12} /> Ou scanner un document
              </button>
            )}
            <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.csv" className="hidden"
              onChange={(e) => e.target.files?.[0] && handleSingleFileUpload(e.target.files[0])} />
            <InvoiceForm form={form} u={u} typeMap={typeMap} recalcTva={recalcTva} />
            <FormButtons onClose={onClose} onSave={handleSave} saving={saving} label={editData ? "Mettre à jour" : "Enregistrer"} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default SupplierInvoiceDialog;
